import { PreFooterLinks } from '../../../../components/PreFooterLinks/PreFooterLinks';
import { TOP_SEARCHED_CITIES } from '../../../../constants/cities/topSearchedWaters';
import { IStateObject } from '../../../../constants/USstates/constants';
import { addDefiniteArticleToCountryName } from '../../../../helpers/country/addDefiniteArticleToCountryName';
import { getNodesFromConnection } from '../../../../helpers/graphql';
import { getCountryUrl, getPlaceUrl, getRegionUrl, getSpeciesUrl } from '../../../../helpers/urls';
import { GetPlace_entry_country } from '../../../../interfaces/graphql';
import { PLACE_SUB_LINK_IDS } from '../../helpers/getPlaceSubNavLinks';

interface IProps {
  country: GetPlace_entry_country | null;
  states: IStateObject[];
}

/**
 * Component that renders a section with links to top cities, species and regions.
 * This is mainly a SEO module and is used for interlinking.
 *
 * For the top cities, we use a hardcoded list of top searched cities.
 *
 * @param {IProps} props - The properties object.
 * @param {GetPlace_entry_country | null} props.country - The country object containing fishing water data.
 * @param {IStateObject[]} props.states - The states object containing top states data.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @component
 * @example
 * const country = {
 *   id: 'US',
 *   countryName: 'United States',
 *   topFishingWaters: { ... },
 *   topSpecies: { ... }
 * };
 *
 * <PreFooterLinksSection country={country} states={states} />
 */
export const PreFooterLinksSection = ({ country, states }: IProps) => {
  const countryId = country?.id;
  const countryName = country?.countryName;
  const topCities = [
    ...TOP_SEARCHED_CITIES.map(({ name, regionCode, slug }) => ({
      id: name,
      name: name,
      url: countryId ? getPlaceUrl(countryId, regionCode, slug) : '',
    })),
    ...[
      {
        id: 'more-cities',
        name: `More ${countryId} cities`,
        url: countryId && countryName ? getCountryUrl(countryId, countryName) : '',
        className: 'font-semibold',
      },
    ],
  ];
  const topSpeciesByCountry = [
    ...getNodesFromConnection(country?.topSpecies).map(species => ({
      id: species._id,
      name: species.displayName,
      url: getSpeciesUrl(species._id, species.displayName),
    })),
    ...[
      {
        id: 'explore-species',
        name: 'Explore species',
        url: '/discover/species',
        className: 'font-semibold',
      },
    ],
  ];
  const topStatesByCountry =
    countryId && countryName && states.length > 0
      ? [
          ...states.map(state => ({
            id: state.stateID,
            name: state.stateName,
            url: getRegionUrl(countryId, state.stateID, countryName, state.stateName),
          })),
          ...[
            {
              id: 'fishing-spots',
              name: 'Fishing spots near you',
              url: '/features/fishing-spots',
              className: 'font-semibold',
            },
          ],
        ]
      : [];

  if (!countryName || !countryId) {
    return <></>;
  }

  const linkList = [
    {
      linkList: topCities,
      headingText: `Popular fishing destinations in ${addDefiniteArticleToCountryName(countryName, countryId)}`,
    },
    {
      linkList: topSpeciesByCountry,
      headingText: `Top species in ${addDefiniteArticleToCountryName(countryName, countryId)}`,
    },
    {
      linkList: topStatesByCountry,
      headingText: `Top regions in ${addDefiniteArticleToCountryName(countryName, countryId)}`,
    },
  ];

  return <PreFooterLinks id={PLACE_SUB_LINK_IDS.EXPLORE_MORE} linkLists={linkList} />;
};
